/**
 * WaitlistForm: the "Stay updated" email capture on the apex home page.
 *
 * Posts to the server's POST /api/mailing-list endpoint (#205), which writes
 * the address to the MailingList collection. The server is the source of
 * truth for validation and duplicate detection; this component still
 * validates client-side too so a malformed address never round-trips.
 *
 * Anti-spam: a honeypot field (`referralCode`) that is visually hidden and
 * removed from the tab order (`tabIndex={-1}`) so real users never see or
 * reach it; bots that autofill every field trip it. It's deliberately named
 * and labeled away from any real address-form field (e.g. "Company"), which
 * browser autofill recognizes and can silently populate even in a hidden
 * field. A tripped honeypot renders the same success state as a real
 * submission (rather than an error) so it gives a scripted submitter no
 * signal to react to, and short-circuits before the request is even sent
 * (the server enforces the same rule independently, so a bot that skips this
 * client entirely is still caught). Submitting also disables the button for
 * the duration of the request, which throttles rapid repeat submissions from
 * the same session.
 *
 * Confirmation announcement: the `role="status"` live region below is always
 * mounted (empty and visually hidden until success) rather than inserted
 * fresh on success, because a live region must already be present in the
 * accessibility tree before its content changes for screen readers to
 * reliably announce it. Focus moves there on success too, since the submit
 * button the user just activated is hidden along with the rest of the form.
 */
import React, { useEffect, useId, useRef, useState } from 'react';
import { Form, Button, Spinner } from 'react-bootstrap';

import { subscribeToMailingList } from '@/shared/services/mailingListApi';

import './WaitlistForm.css';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STATUS_IDLE = 'idle';
const STATUS_SUBMITTING = 'submitting';
const STATUS_SUCCESS = 'success';
const STATUS_ERROR = 'error';

export default function WaitlistForm() {
    const [email, setEmail] = useState('');
    const [referralCode, setReferralCode] = useState(''); // honeypot
    const [status, setStatus] = useState(STATUS_IDLE);
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const errorId = useId();
    const statusRef = useRef(null);

    const isSuccess = status === STATUS_SUCCESS;

    useEffect(() => {
        if (isSuccess) {
            statusRef.current?.focus();
        }
    }, [isSuccess]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!EMAIL_PATTERN.test(email.trim())) {
            setStatus(STATUS_ERROR);
            setErrorMessage(
                'Enter a valid email address, like jane.doe@example.com.'
            );
            return;
        }

        // Honeypot tripped: look successful to whatever filled it in, but
        // never actually submit.
        if (referralCode.trim() !== '') {
            setSuccessMessage("Thanks — you're on the list!");
            setStatus(STATUS_SUCCESS);
            return;
        }

        setStatus(STATUS_SUBMITTING);
        setErrorMessage('');

        try {
            const data = await subscribeToMailingList({
                email: email.trim(),
                referralCode,
            });
            setSuccessMessage(data.message || "Thanks — you're on the list!");
            setStatus(STATUS_SUCCESS);
        } catch (err) {
            setStatus(STATUS_ERROR);
            setErrorMessage(
                err.message ||
                    "Something went wrong and we couldn't add you to the list. Please try again."
            );
        }
    };

    const isSubmitting = status === STATUS_SUBMITTING;
    const hasError = status === STATUS_ERROR;

    return (
        <>
            <div
                ref={statusRef}
                role="status"
                tabIndex={-1}
                className={
                    isSuccess ? 'alert alert-success mb-0' : 'visually-hidden'
                }
            >
                {isSuccess ? successMessage : ''}
            </div>
            {!isSuccess && (
                <Form
                    noValidate
                    onSubmit={handleSubmit}
                    className="waitlist-form-card bg-white border rounded-3 p-4"
                >
                    <Form.Group controlId="waitlist-email">
                        <Form.Label className="small waitlist-form-label">
                            Email address
                        </Form.Label>
                        <div className="d-flex flex-column flex-sm-row gap-2">
                            <Form.Control
                                type="email"
                                placeholder="jane.doe@example.com"
                                value={email}
                                onChange={(e) => {
                                    setEmail(e.target.value);
                                    // Clear a stale error as soon as the user
                                    // starts correcting the address, rather
                                    // than leaving the invalid state up until
                                    // the next submit attempt.
                                    if (hasError) {
                                        setStatus(STATUS_IDLE);
                                        setErrorMessage('');
                                    }
                                }}
                                isInvalid={hasError}
                                aria-invalid={hasError}
                                aria-describedby={
                                    hasError ? errorId : undefined
                                }
                                required
                            />
                            {/* Honeypot: hidden from sighted users via CSS,
                                but not display:none, so it stays reachable by
                                naive bots that fill in every field regardless
                                of visibility. */}
                            <Form.Group
                                controlId="waitlist-referral"
                                className="waitlist-form-honeypot"
                                aria-hidden="true"
                            >
                                <Form.Label>Referral code</Form.Label>
                                <Form.Control
                                    type="text"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    value={referralCode}
                                    onChange={(e) =>
                                        setReferralCode(e.target.value)
                                    }
                                />
                            </Form.Group>
                            <Button
                                type="submit"
                                variant="primary"
                                disabled={isSubmitting}
                                className="text-nowrap"
                            >
                                {isSubmitting ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        Subscribing…
                                    </>
                                ) : (
                                    'Subscribe'
                                )}
                            </Button>
                        </div>
                        {hasError && (
                            <Form.Text
                                id={errorId}
                                className="text-danger d-block mt-2"
                            >
                                {errorMessage}
                            </Form.Text>
                        )}
                    </Form.Group>
                </Form>
            )}
        </>
    );
}
